import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import React, { useEffect, useMemo, useRef } from 'react';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmailBodyEditorProps {
  value: string;
  onChange: (_value: string) => void;
  error?: string;
  placeholder?: string;
}

export const EmailBodyEditor: React.FC<EmailBodyEditorProps> = ({
  value,
  onChange,
  error,
  placeholder,
}) => {
  const isInternalChange = useRef(false);
  const previousValueRef = useRef(value);
  const isReadyRef = useRef(false);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        underline: false,
      }),
      Underline,
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: value || '',
    onUpdate: ({ editor: current }) => {
      if (!isReadyRef.current) return;
      isInternalChange.current = true;
      onChange(current.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'min-h-[193px] flex-1 px-3.5 py-3.5 font-sans text-sm font-normal leading-5 text-[#0A0A0A] focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    isReadyRef.current = true;
    if (value && editor.getHTML() !== value) {
      previousValueRef.current = value;
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor]);

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      previousValueRef.current = value;
      return;
    }

    if (previousValueRef.current === value || !editor) return;

    if (editor.getHTML() !== value) {
      previousValueRef.current = value;
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const isEmpty = editor.isEmpty;

  return (
    <div
      className={cn(
        'relative flex min-h-[290px] w-full flex-1 flex-col overflow-hidden rounded-lg border bg-white',
        error ? 'border-red-500' : 'border-[#E5E5E5]'
      )}
    >
      <div className="flex items-center gap-[3.5px] border-b border-black/10 bg-[#F9FAFB] p-[7px]">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-[6.75px] hover:bg-gray-200',
            editor.isActive('bold') && 'bg-gray-200'
          )}
        >
          <Bold className="h-3.5 w-3.5 text-[#0A0A0A]" strokeWidth={1.17} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-[6.75px] hover:bg-gray-200',
            editor.isActive('italic') && 'bg-gray-200'
          )}
        >
          <Italic className="h-3.5 w-3.5 text-[#0A0A0A]" strokeWidth={1.17} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-[6.75px] hover:bg-gray-200',
            editor.isActive('underline') && 'bg-gray-200'
          )}
        >
          <UnderlineIcon className="h-3.5 w-3.5 text-[#0A0A0A]" strokeWidth={1.17} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-[6.75px] hover:bg-gray-200',
            editor.isActive('bulletList') && 'bg-gray-200'
          )}
        >
          <List className="h-3.5 w-3.5 text-[#0A0A0A]" strokeWidth={1.17} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-[6.75px] hover:bg-gray-200',
            editor.isActive('orderedList') && 'bg-gray-200'
          )}
        >
          <ListOrdered className="h-3.5 w-3.5 text-[#0A0A0A]" strokeWidth={1.17} />
        </button>
      </div>
      <div className="relative flex-1">
        {placeholder && isEmpty ? (
          <p className="pointer-events-none absolute left-3.5 top-3.5 font-sans text-sm font-normal leading-5 text-[#737373]">
            {placeholder}
          </p>
        ) : null}
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .ProseMirror { outline: none; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin: 0.5rem 0; }
        .ProseMirror ul { list-style-type: disc; }
        .ProseMirror ol { list-style-type: decimal; }
        .ProseMirror p { margin: 0.75rem 0; }
        .ProseMirror p:first-child { margin-top: 0; }
        .ProseMirror p:last-child { margin-bottom: 0; }
      `}</style>
    </div>
  );
};
